import type { EventDetailMarketDto } from '@/lib/models';

export interface MarketRulesContentProps {
  /** Markets whose `marketRules` (Plaee outcomeRules json) to render. */
  markets: EventDetailMarketDto[];
}

/** A defensively-typed view of the Plaee rulebook json. */
interface RuleShape {
  ruleDescription?: unknown;
  description?: unknown;
  timelineSteps?: unknown;
  sections?: unknown;
}

/** Coerce an unknown json value to a trimmed string, or `null`. */
function asText(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

/**
 * Render the resolution rules text for a market (ARC-82 §5.4.4).
 *
 * `marketRules` is an untyped Plaee `outcomeRules` json blob — typically
 * `{ ruleDescription, timelineSteps?, sections? }` but shapes vary. Renders
 * defensively: top-level description, then any string-bearing `sections` /
 * `timelineSteps`. Falls back to a no-rules message when nothing is parseable.
 */
export function MarketRulesContent({ markets }: MarketRulesContentProps) {
  const blocks: { key: string; heading: string | null; body: string }[] = [];

  markets.forEach((market, mi) => {
    const rules = market.marketRules as RuleShape | null;
    if (!rules || typeof rules !== 'object') return;

    const heading = markets.length > 1 ? (market.displayName ?? `Market ${mi + 1}`) : null;
    const description = asText(rules.ruleDescription) ?? asText(rules.description);
    if (description) blocks.push({ key: `${market.id}-desc`, heading, body: description });

    const sections = Array.isArray(rules.sections) ? rules.sections : [];
    sections.forEach((s, si) => {
      const text = asText(s) ?? asText((s as { text?: unknown })?.text) ?? asText((s as { body?: unknown })?.body);
      const subHeading = asText((s as { title?: unknown })?.title);
      if (text) blocks.push({ key: `${market.id}-sec-${si}`, heading: subHeading ?? heading, body: text });
    });

    const steps = Array.isArray(rules.timelineSteps) ? rules.timelineSteps : [];
    steps.forEach((step, sti) => {
      const text = asText(step) ?? asText((step as { label?: unknown })?.label);
      if (text) blocks.push({ key: `${market.id}-step-${sti}`, heading: null, body: text });
    });
  });

  if (blocks.length === 0) {
    return <p className="text-sm text-muted">Resolution rules are not available for this market yet.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block) => (
        <div key={block.key} className="flex flex-col gap-1">
          {block.heading && <h4 className="text-sm font-semibold text-primary m-0">{block.heading}</h4>}
          <p className="text-sm text-primary/80 whitespace-pre-line m-0">{block.body}</p>
        </div>
      ))}
    </div>
  );
}
